import { OpenImgContextProvider } from 'openimg/react'
import { useTranslation } from 'react-i18next'
import {
	data,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { type Route } from './+types/root.ts'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import { brand } from './brand.ts'
import { AppShellBoundary } from './components/app-shell.tsx'
import { ConsentBanner } from './components/consent-banner.tsx'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { ImpersonationBanner } from './components/impersonation-banner.tsx'
import { Matomo } from './components/matomo.tsx'
import { NotificationStreamProvider } from './components/notifications/notification-stream.tsx'
import { RoutePendingProvider } from './components/route-pending.tsx'
import { useToast } from './components/toaster.tsx'
import { href as iconsHref } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import {
	getLocale,
	i18nextMiddleware,
	localeCookie,
} from './middleware/i18next.ts'
import {
	useOptimisticAccent,
	useOptimisticButtonCursor,
} from './routes/resources/accent.tsx'
import { useOptimisticConsentState } from './routes/resources/consent.tsx'
import { useOptionalTheme, useTheme } from './routes/resources/theme-switch.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getAccent } from './utils/accent.server.ts'
import { accentVars, DEFAULT_ACCENT } from './utils/accent.ts'
import { getImpersonator, getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { getConsentMode, resolveConsent } from './utils/consent.server.ts'
import { analyticsAllowed } from './utils/consent.ts'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { pipeHeaders } from './utils/headers.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { toLocale } from './utils/i18n.ts'
import {
	combineHeaders,
	getDomainUrl,
	getImgSrc,
	getRequestUrl,
} from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { getUnreadCount } from './utils/notification.server.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import { displayName } from './utils/user.ts'

// The i18next middleware resolves the UI locale for every request (cookie →
// Accept-Language → `de`) and exposes it to loaders + `entry.server` via the
// module's accessors. Registered on root, so it runs for the whole tree — this
// is the repo's first production React Router middleware (ADR-074).
export const middleware = [i18nextMiddleware]

// Routes declare the i18n namespaces they render; the client loads their union
// lazily (see `entry.client.tsx`). Root anchors the always-present `common`,
// plus `validation`: any route can render form errors, and client revalidation
// must always produce the same copy the server rendered (EPT-128).
export const handle = { i18n: ['common', 'validation'] } as const

export const links: Route.LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ data }) => {
	// The loader surfaces the locale the middleware resolved; select the brand's
	// per-locale copy off it (ADR-074). An errored request has no data — `toLocale`
	// degrades to the Default Language rather than leaking a fixed English string.
	const locale = toLocale(data?.locale ?? '')
	const description = brand.description[locale]
	return [
		{ title: data ? brand.appName : `Error | ${brand.appName}` },
		{ name: 'description', content: description },
		{
			property: 'og:title',
			content: `${brand.appName} — ${brand.tagline[locale]}`,
		},
		{ property: 'og:description', content: description },
	]
}

export async function loader({ request, context }: Route.LoaderArgs) {
	const timings = makeTimings('root loader')
	// The middleware already resolved the locale; read it through the accessor
	// (never off the request directly) so detection stays in one place.
	const locale = getLocale(context)
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUnique({
						select: {
							id: true,
							name: true,
							email: true,
							image: { select: { objectKey: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	// The bell's unread tally, SSR-fed so the badge is correct on first paint and
	// refreshes on navigation (EPT-157, ADR-078); the dropdown fetcher and the SSE
	// stream (EPT-160) layer live updates on top. `null` for a guest — no bell.
	const unreadCount = user ? await getUnreadCount(user.id) : null
	// While impersonating, `user` above is the target; the real actor rides
	// alongside so the chrome (banner, EPT-108) is SSR-fed. Resolved identity is
	// memoized per request — this adds no extra session lookup.
	const impersonator = await getImpersonator(request)
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = await honeypot.getInputProps()

	return data(
		{
			user,
			unreadCount,
			impersonator,
			locale,
			// The visitor's Analytics Consent (ADR-075), resolved server-side off
			// the `en_consent` cookie so the banner is SSR-present and `<Matomo/>`
			// mounts only with recorded consent. The deploy-time posture rides
			// alongside (resolved once) so the mount gate needs no client-side cast.
			consent: resolveConsent(request),
			consentMode: getConsentMode(),
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: getRequestUrl(request).pathname,
				userPrefs: {
					theme: getTheme(request),
					accent: getAccent(request)?.accent ?? null,
					cursor: getAccent(request)?.cursor ?? 'default',
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
				// Persist the resolved locale so an explicit choice sticks on this
				// browser and later requests skip straight to it (ADR-074).
				{ 'Set-Cookie': await localeCookie.serialize(locale) },
			),
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

function Document({
	children,
	nonce,
	theme = 'light',
	accentStyle,
	env = {},
	trackingConsented = false,
	cookieless = false,
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	accentStyle?: React.CSSProperties
	env?: Record<string, string | undefined>
	trackingConsented?: boolean
	cookieless?: boolean
}) {
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'
	// The resolved UI locale (SSR: the request instance; client: read back from
	// `<html lang>` via htmlTag detection), so the document always declares the
	// language the chrome is actually rendered in (ADR-074).
	const { i18n } = useTranslation()
	return (
		<html
			lang={i18n.language}
			dir={i18n.dir(i18n.language)}
			className={`${theme} h-full`}
			style={accentStyle}
		>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
			</head>
			<body className="bg-background text-foreground overflow-x-hidden">
				{children}
				{env.MATOMO_URL && env.MATOMO_SITE_ID && trackingConsented ? (
					<Matomo
						url={env.MATOMO_URL}
						siteId={env.MATOMO_SITE_ID}
						cookieless={cookieless}
					/>
				) : null}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	const theme = useOptionalTheme()
	// Apply the chosen accent as inline CSS vars on <html> server-side (no flash),
	// re-tinting optimistically while an accent switch is in flight (ADR 062).
	const accent =
		useOptimisticAccent(data?.requestInfo.userPrefs.accent ?? undefined) ??
		DEFAULT_ACCENT
	// The button-cursor pref rides the same inline-vars channel: `--btn-cursor`
	// flips the customizer's cursor segment app-wide, server-applied (no flash).
	const cursor =
		useOptimisticButtonCursor(
			data?.requestInfo.userPrefs.cursor ?? undefined,
		) ?? 'default'
	const accentStyle = {
		...accentVars(accent),
		'--btn-cursor': cursor,
	} as React.CSSProperties
	// Gate the analytics mount on recorded consent (ADR-075), flipping
	// optimistically the moment the visitor accepts/declines so the tracker
	// boots or stays off before the network settles. Under the cookieless posture
	// Matomo runs by default but with cookies disabled.
	const consentMode = data?.consentMode ?? 'gated'
	const consentState =
		useOptimisticConsentState() ?? data?.consent?.state ?? 'unset'
	const trackingConsented = analyticsAllowed(consentState, consentMode)
	return (
		<Document
			nonce={nonce}
			theme={theme}
			accentStyle={accentStyle}
			env={data?.ENV}
			trackingConsented={trackingConsented}
			cookieless={consentMode === 'cookieless'}
		>
			{children}
		</Document>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const theme = useTheme()
	useToast(data.toast)

	// root.tsx no longer renders any generic chrome (ADR-068, EPT-78): every
	// surface owns its frame explicitly — non-marketing sections via `AppShell`,
	// the marketing surfaces via their own branded layout. root just supplies the
	// document shell, the image optimizer context, and the toaster/progress chrome.
	//
	// The one exception is the impersonation banner (EPT-108): it frames *every*
	// surface, so it sits here above the outlet — and thus above each surface's
	// AppShell navbar. It renders only while impersonating (`name` is the target's
	// display name; `null` otherwise).
	return (
		<OpenImgContextProvider
			optimizerEndpoint="/resources/images"
			getSrc={getImgSrc}
		>
			<ImpersonationBanner
				name={data.impersonator && data.user ? displayName(data.user) : null}
			/>
			<ConsentBanner consent={data.consent} mode={data.consentMode} />
			{/* Live notification delivery (EPT-160): a single app-level SSE stream that
			    feeds the bell, toast, and aria-live region. Opens only for a signed-in
			    user; wraps the outlet so the bell (in each surface's AppShell) reads it. */}
			{/* Route-pending chrome (EPT-193): the under-navbar loading bar + shared
			    "Loading" announcer render here, and the pending flag flows through
			    context to each surface's AppShell (content dim + logo halo). */}
			<RoutePendingProvider>
				<NotificationStreamProvider userId={data.user?.id ?? null}>
					<Outlet />
				</NotificationStreamProvider>
			</RoutePendingProvider>
			<EpicToaster closeButton position="top-center" theme={theme} />
		</OpenImgContextProvider>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default AppWithProviders

// The last-resort error boundary for anything not caught by a section or the
// `/` splat. It renders inside the unified `AppShell` (`full`) so even an
// unexpected error has the universal navbar to escape from (ADR-068, EPT-78).
export function ErrorBoundary() {
	return (
		<AppShellBoundary>
			<GeneralErrorBoundary />
		</AppShellBoundary>
	)
}
